import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { vendorApi } from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusTimeline from '../../components/common/StatusTimeline';
import PhotoThumb from '../../components/common/PhotoThumb';
import NoteThread from '../../components/common/NoteThread';
import AuditLogPanel from '../../components/common/AuditLogPanel';
import { statusLabel } from '../../utils/statusLabels';
import { downloadBomComponentsExcel } from '../../utils/bomExcelExport';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { selectCompanyName, selectUserRole } from '../../store/slices/authSlice';
import PartArticleExcelUpload from './PartArticleExcelUpload';

/** Returns true for 15- or 18-character Salesforce IDs — never pre-populate or display these. */
function isSalesforceId(value) {
  return typeof value === 'string' && /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/.test(value);
}

const UOM_OPTIONS = ['EACH', 'SET', 'PCS', 'PAIR', 'BOX', 'ROLL', 'MTR', 'KG', 'LTR'];
const MEASUREMENT_UNIT_OPTIONS = ['INCHES', 'CENTIMETER'];
const normalizeMeasurementUnit = (value) => {
  const unit = String(value ?? '').trim().toUpperCase();
  if (unit === 'IN') return 'INCHES';
  if (unit === 'CM') return 'CENTIMETER';
  return unit;
};
const MAX_PHOTOS = 4;

const BOM_FIELD_ORDER = [
  'partName', 'partDescription', 'stockType', 'partColor', 'measurementUnit',
  'length', 'width', 'height', 'weightUnit', 'partWeight', 'category',
  'vendorPartNumber', 'vendorFactoryName', 'supplierName', 'supplierPartNumber',
  'uom', 'totalQtyInFg', 'costPerUom',
];

/** Returns true if a value is a placeholder dash — not accepted in any BOM field */
const isDashValue = (v) => typeof v === 'string' && /^\s*[-–—]+\s*$/.test(v.trim());

/** Returns true if a non-empty value contains no letters or digits (pure special characters) */
const isSpecialCharsOnly = (v) => typeof v === 'string' && v.trim().length > 0 && /^[^a-zA-Z0-9]+$/.test(v.trim());
const isAlphabeticText = (v) => /^[A-Za-z ]+$/.test(String(v ?? '').trim());
const isNumericDimension = (v) => /^\d+(\.\d{1,2})?$/.test(String(v ?? '').trim());
const isEditableDecimal = (v) => /^\d*(?:\.\d{0,2})?$/.test(v);

/** Returns an inline error message if the value is invalid, or null if OK */
const getFieldError = (v, required) => {
  if (!v || !v.trim()) return required ? 'Required' : null;
  if (isDashValue(v)) return 'Dashes not allowed';
  if (isSpecialCharsOnly(v)) return 'Special characters only not allowed';
  return null;
};

const STOCK_TYPE_OPTIONS = ['STOCK', 'SPECIAL'];

const WEIGHT_UNIT_OPTIONS = [
  { value: 'lb', label: 'Pounds (lb)' },
  { value: 'oz', label: 'Ounces (oz)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'g',  label: 'Grams (g)' },
];

const normalizeStockType = (value) => String(value ?? '').trim().toUpperCase() || 'STOCK';
const normalizeWeightUnit = (value) => {
  const unit = String(value ?? '').trim().toLowerCase();
  if (unit === 'lb' || unit.includes('pound')) return 'lb';
  if (unit === 'oz' || unit.includes('ounce')) return 'oz';
  if (unit === 'kg' || unit.includes('kilogram')) return 'kg';
  if (unit === 'g' || unit.includes('gram')) return 'g';
  return unit || 'lb';
};

const CATEGORY_OPTIONS = [
  { group: 'Fabric & Cores', items: ['Casing', 'Full Cushion', 'Core', 'Toss Pillow', 'Back Pillow', 'Leather'] },
  { group: 'Wood', items: ['Drawers', 'Panels', 'Full Wood Chair', 'Wood Legs', 'Console & Lid'] },
  { group: 'Electronics', items: [
    'Motors/Actuator', 'Massage Motor', 'Switches', 'Hand Wand', 'Control Pad',
    'Wireless Charger', 'USB Charger', 'Transformer', 'Cup Holder', 'Control Box',
    'Remote Control', 'Kit - Control Box/Remote', 'Compressor', 'Airbag', 'Y-Cable',
    'Extension Cable', 'Power Cable', 'Other Cable', 'Power Outlet', 'Fuse', 'Socket',
    'Reading Light', 'Speakers', 'LED Light', 'Sensor Buttons', 'Battery', 'Other Electronics',
  ]},
  { group: 'Mechanisms', items: [
    'Mechanism Scissors', 'Sleeper - Queen', 'Sleeper - Full', 'Sleeper - Twin',
    'Mechanism Tubes', 'Mechanism Springs', 'Headrest Mechanism', 'Lumbar Mechanism',
    'Headrest/Lumbar Mechanism Kit', 'Mechanism Hardware', 'Mechanism Base', 'Other Mechanisms',
  ]},
  { group: 'Hardware', items: [
    'Pull Handle', 'Storage Hinge/Mech', 'Bracket', 'Side Glides', 'Underneath Glide',
    'Headrest Latch', 'Seat Springs', 'Soft Close Piston', 'Caster Wheel',
    'Drawer/Door Handle', 'Drawer/Door Knob', 'Rubber Coaster', 'Footrest Plastic/Metal Support',
    'Power Motion Connector', 'Stationary Connector', 'Tray Cover - Plastic',
    'Tray Cover with Wireless Charger', 'Electronic Hardware', 'Heating Pad',
    'Pole / Rod / Stand', 'Metal Sofa Legs', 'Plastic Sofa Legs', 'Other Hardware',
  ]},
];

const CATEGORY_VALUES = CATEGORY_OPTIONS.flatMap(({ group, items }) =>
  items.map(item => ({ value: `${group} > ${item}`, item }))
);
const normalizeCategory = (value) => {
  const category = String(value ?? '').trim();
  if (!category) return '';
  const comparable = category.toUpperCase();
  return CATEGORY_VALUES.find(({ value: option, item }) =>
    option.toUpperCase() === comparable || item.toUpperCase() === comparable
  )?.value || category;
};

const emptyComponent = () => ({
  _key: `${Date.now()}-${Math.random()}`,
  id: null,
  articleNumber: '',     // auto-generated, shown read-only
  partName: '',
  partDescription: '',
  stockType: 'STOCK',
  partColor: '',
  length: '',
  width: '',
  height: '',
  measurementUnit: 'INCHES',
  partWeight: '',
  weightUnit: 'lb',
  category: '',
  vendorPartNumber: '',
  vendorFactoryName: '',
  supplierName: '',
  supplierPartNumber: '',
  uom: 'EACH',
  totalQtyInFg: '',
  costPerUom: '',
  currency: 'USD',
  pendingPhotos: [],     // array of { file, url }
});

const uppercaseImportedText = (value) => String(value ?? '').toUpperCase();

export default function PartArticleForm() {
  const { id }        = useParams();
  const navigate      = useNavigate();
  const qc            = useQueryClient();
  const vendorCompany = useSelector(selectCompanyName);
  const userRole      = useSelector(selectUserRole);

  const [mainProduct, setMainProduct] = useState({
    fgArticleId: '', collectionName: '', productDescription: '',
    articleGroup: '', genericStyle: '',
  });
  const [mainPendingPhotos, setMainPendingPhotos] = useState([]); // array of { file, url }
  const [components, setComponents]   = useState([emptyComponent()]);
  const [confirmSubmit, setConfirmSubmit]   = useState(false);
  const [errorMessages, setErrorMessages]   = useState(null); // array of strings or null
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [fieldProgress, setFieldProgress] = useState({});
  const initialisedRef                      = useRef(false);
  const [submitting, setSubmitting]         = useState(false);
  const [savingDraft, setSavingDraft]       = useState(false);
  const [removingPhotoIds, setRemovingPhotoIds] = useState([]);
  // Tracks the last-saved category per saved component id (for dirty detection)
  const savedCategoriesRef                  = useRef({});

  const isInvalidText = (v) => !v?.trim() || isDashValue(v) || isSpecialCharsOnly(v);

  // All BOM fields are now mandatory; dashes and special-character-only values are not accepted
  const isFormValid = components.length > 0 && components.every(c =>
    !isInvalidText(c.partName) &&
    !isInvalidText(c.partDescription) &&
    c.stockType &&
    !isInvalidText(c.partColor) &&
    isAlphabeticText(c.partColor) &&
    [c.length, c.width, c.height].every(isNumericDimension) &&
    MEASUREMENT_UNIT_OPTIONS.includes(c.measurementUnit) &&
    !isInvalidText(c.partWeight) &&
    isNumericDimension(c.partWeight) &&
    c.weightUnit &&
    c.category &&
    !isInvalidText(c.vendorPartNumber) &&
    !isInvalidText(c.vendorFactoryName) &&
    !isInvalidText(c.supplierName) &&
    !isInvalidText(c.supplierPartNumber) &&
    c.uom &&
    String(c.totalQtyInFg).trim() !== '' &&
    String(c.costPerUom).trim() !== ''
  );

  const { data, isLoading } = useQuery(
    ['vendor-assignment', id],
    () => vendorApi.getAssignment(id),
    { refetchOnWindowFocus: false }
  );
  const assignment = data?.data;
  // Vendor users have the same BOM permissions as vendor admins. Company-user
  // management remains restricted to VENDOR_ADMIN elsewhere in the portal.
  const canManageSetupSheet = ['VENDOR_ADMIN', 'VENDOR_USER'].includes(userRole);
  const canEdit = canManageSetupSheet && assignment && ['ASSIGNED', 'COMPANY_REJECTED', 'CLIENT_REJECTED', 'CVA_REJECTED'].includes(assignment.status);
  const submittedBomParts = assignment?.partArticles?.filter(p => !p.mainProduct) || [];
  const canDownloadSubmittedBom = Boolean(assignment?.submittedAt && submittedBomParts.length > 0);
  // Category stays editable at any status except final client approval
  const canEditCategory = canManageSetupSheet && assignment && assignment.status !== 'CLIENT_APPROVED';

  const handleDownloadBom = () => {
    try {
      downloadBomComponentsExcel({
        components: submittedBomParts,
        finishedGoodCode: assignment.bobFinishedGoodCode || assignment.fgName,
      });
      toast.success('BOM downloaded successfully.');
    } catch {
      toast.error('Could not download the BOM Excel file. Please try again.');
    }
  };

  // Cleanup object URLs on unmount
  useEffect(() => () => {
    mainPendingPhotos.forEach(p => URL.revokeObjectURL(p.url));
    components.forEach(c => c.pendingPhotos.forEach(p => URL.revokeObjectURL(p.url)));
  }, []); // eslint-disable-line

  // ── Initialise from saved data ────────────────────────────────────────
  useEffect(() => {
    if (!assignment || initialisedRef.current) return;
    initialisedRef.current = true;

    const fgParts  = assignment.partArticles?.filter(p => p.mainProduct)  || [];
    const bomParts = assignment.partArticles?.filter(p => !p.mainProduct) || [];
    const mp = fgParts[0];

    setMainProduct({
      fgArticleId:        (mp?.fgArticleId && !isSalesforceId(mp.fgArticleId)) ? mp.fgArticleId : '',
      collectionName:     mp?.collectionName      || assignment.collectionName       || '',
      productDescription: mp?.productDescription  || assignment.productDescription   || '',
      articleGroup:       mp?.articleGroup        || '',
      genericStyle:       mp?.genericStyle        || '',
    });

    // For non-editable statuses with no saved BOM parts, show empty list instead of a blank placeholder row
    const isEditable = ['ASSIGNED', 'COMPANY_REJECTED', 'CLIENT_REJECTED', 'CVA_REJECTED'].includes(assignment.status);
    if (bomParts.length === 0 && !isEditable) {
      setComponents([]);
      return;
    }

    if (bomParts.length > 0) {
      const mapped = bomParts.map(c => ({
        _key:             String(c.id),
        id:               c.id,
        articleNumber:    c.articleNumber       || '',
        partName:             c.partName              || '',
        partDescription:      c.articleName           || '',
        stockType:            normalizeStockType(c.stockType),
        partColor:            c.partColor             || '',
        length:               c.length                || '',
        width:                c.width  || (c.measurement && !c.length && !c.height ? c.measurement : '') || '',
        height:               c.height                || '',
        measurementUnit:      normalizeMeasurementUnit(c.measurementUnit || 'INCHES'),
        partWeight:           c.partWeight            || '',
        weightUnit:           normalizeWeightUnit(c.weightUnit),
        category:             normalizeCategory(c.category),
        vendorPartNumber:     uppercaseImportedText(c.vendorPartNumber),
        vendorFactoryName:    c.vendorFactoryName     || '',
        supplierName:         c.supplierName          || '',
        supplierPartNumber:   uppercaseImportedText(c.supplierPartNumber),
        uom:                  c.uom                   || 'EACH',
        totalQtyInFg:         c.totalQtyInFg          ?? '',
        costPerUom:           c.unitPrice             ?? '',
        currency:             c.currency              || 'USD',
        pendingPhotos:        [],
      }));
      // Snapshot the server-persisted categories so we can detect dirty state
      savedCategoriesRef.current = Object.fromEntries(mapped.map(c => [c.id, c.category]));
      setComponents(mapped);
    }
  }, [assignment]);

  // ── Helpers ───────────────────────────────────────────────────────────
  const buildPayload = (componentRows = components) => ({
    assignmentId: Number(id),
    mainProduct: { ...mainProduct },
    components: componentRows.map(c => ({
      id:                     c.id,
      partName:               c.partName,
      partDescription:        c.partDescription,
      stockType:              normalizeStockType(c.stockType),
      partColor:              c.partColor,
      length:                 c.length,
      width:                  c.width,
      height:                 c.height,
      measurementUnit:        c.measurementUnit,
      partWeight:             c.partWeight,
      weightUnit:             normalizeWeightUnit(c.weightUnit),
      category:               normalizeCategory(c.category),
      vendorPartNumber:       c.vendorPartNumber,
      vendorFactoryName:      c.vendorFactoryName,
      supplierName:           c.supplierName,
      supplierPartNumber:     c.supplierPartNumber,
      uom:                    c.uom,
      totalQtyInFg:           c.totalQtyInFg !== '' ? Number(c.totalQtyInFg) : null,
      costPerUom:             c.costPerUom   !== '' ? Number(c.costPerUom)   : null,
      currency:               c.currency,
    })),
  });

  // ── Save for Later: saves draft text fields + uploads any pending photos ──
  const handleSaveForLater = async () => {
    const componentsToSave = components;
    setSavingDraft(true);
    try {
      // 1. Save text fields as draft
      const draftRes = await vendorApi.saveAsDraft(id, buildPayload(componentsToSave));
      const savedParts = draftRes?.data?.partArticles || [];
      const savedMain  = savedParts.find(p => p.mainProduct);
      const savedBom   = savedParts.filter(p => !p.mainProduct);

      const savedBomById = new Map(savedBom.map(component => [component.id, component]));
      const newSavedBom = savedBom.filter(component =>
        !componentsToSave.some(localComponent => localComponent.id === component.id)
      );
      let newSavedBomIndex = 0;
      const savedIdByKey = new Map(componentsToSave.map(component => {
        const savedId = component.id && savedBomById.has(component.id)
          ? component.id
          : newSavedBom[newSavedBomIndex++]?.id;
        return [component._key, savedId ?? component.id];
      }));

      // Keep the IDs assigned by the draft save even when there are no photos.
      // Without these IDs, Remove treats a persisted row as local-only, so it
      // reappears the next time the assignment is loaded.
      setComponents(prev => prev.map(component => ({
        ...component,
        id: savedIdByKey.get(component._key) ?? component.id,
      })));

      // 2. Upload pending main product photos — clear each one immediately after success
      if (savedMain?.id && mainPendingPhotos.length > 0) {
        for (const photo of mainPendingPhotos) {
          await vendorApi.uploadFile(savedMain.id, photo.file);
          setMainPendingPhotos(prev => prev.slice(1));
        }
      }

      // 3. Upload pending BOM component photos — clear each one immediately after success
      for (let i = 0; i < componentsToSave.length; i++) {
        const comp = componentsToSave[i];
        const savedComponentId = savedIdByKey.get(comp._key);
        if (comp.pendingPhotos.length > 0 && savedComponentId) {
          for (const photo of comp.pendingPhotos) {
            await vendorApi.uploadFile(savedComponentId, photo.file);
            setComponents(prev => prev.map(c =>
              c._key === comp._key ? { ...c, pendingPhotos: c.pendingPhotos.slice(1), id: savedComponentId } : c
            ));
          }
        }
      }

      toast.success('Draft saved!');
      qc.invalidateQueries(['vendor-assignment', id]);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };


  const validateForm = () => {
    // Main product photo is mandatory
    const hasMainPhoto = (savedMainArticle?.files?.length || 0) + mainPendingPhotos.length > 0;
    if (!hasMainPhoto) return 'Main Product Photo is required. Please upload at least one photo before submitting.';
    const compsMap = Object.fromEntries(
      (assignment?.partArticles?.filter(p => !p.mainProduct) || []).map(c => [c.id, c])
    );

    for (let i = 0; i < components.length; i++) {
      const c = components[i];
      const badText = (v, label) => {
        if (!v?.trim())            return `Component #${i + 1}: ${label} is required`;
        if (isDashValue(v))        return `Component #${i + 1}: ${label} cannot be "—". Please enter a real value`;
        if (isSpecialCharsOnly(v)) return `Component #${i + 1}: ${label} cannot contain only special characters`;
        return null;
      };
      const e1 = badText(c.partName, 'Part Name');               if (e1) return e1;
      const e2 = badText(c.partDescription, 'Part Description'); if (e2) return e2;
      if (!c.stockType)                     return `Component #${i + 1}: Stock Type is required`;
      const e5 = badText(c.partColor, 'Part Color'); if (e5) return e5;
      if (!isAlphabeticText(c.partColor)) return `Component #${i + 1}: Part Color must contain letters only`;
      if (!String(c.length).trim()) return `Component #${i + 1}: Length is required`;
      if (!String(c.width).trim()) return `Component #${i + 1}: Width is required`;
      if (!String(c.height).trim()) return `Component #${i + 1}: Height is required`;
      if (![c.length, c.width, c.height].every(isNumericDimension)) return `Component #${i + 1}: Length, Width, and Height must contain numbers with up to 2 decimal places`;
      if (!MEASUREMENT_UNIT_OPTIONS.includes(c.measurementUnit)) return `Component #${i +1}: Measurement Unit must be INCHES or CENTIMETER`;
      const e6 = badText(c.partWeight, 'Weight'); if (e6) return e6;
      if (!isNumericDimension(c.partWeight)) return `Component #${i + 1}: Weight must contain numbers with up to 2 decimal places`;
      if (!c.weightUnit)                    return `Component #${i + 1}: Weight Unit is required`;
      if (!c.category)                      return `Component #${i + 1}: Category is required`;
      const e7 = badText(c.vendorPartNumber, 'Vendor Part Number');     if (e7) return e7;
      const e8 = badText(c.vendorFactoryName, "Vendor's Factory Name"); if (e8) return e8;
      const e9 = badText(c.supplierName, 'Supplier Name');              if (e9) return e9;
      const e10 = badText(c.supplierPartNumber, 'Supplier Part Number'); if (e10) return e10;
      if (!c.uom)                           return `Component #${i + 1}: UoM is required`;
      if (c.totalQtyInFg === '')            return `Component #${i + 1}: Total Qty in FG is required`;
      if (c.costPerUom === '')              return `Component #${i + 1}: Cost per UoM is required`;
      const savedCompCount = (c.id ? compsMap[c.id]?.files?.length : 0) || 0;
      if (savedCompCount + c.pendingPhotos.length === 0) return `Component #${i + 1}: At least one photo is required`;
    }
    return null;
  };

  const updateComp      = (key, field, val) => setComponents(prev => prev.map(c => c._key === key ? { ...c, [field]: val } : c));
  const addComponent    = () => setComponents(prev => [...prev, emptyComponent()]);
  const markFieldProgress = (key, field, completed = false) => {
    const index = BOM_FIELD_ORDER.indexOf(field);
    if (index < 0) return;
    const progress = index + (completed ? 1 : 0);
    setFieldProgress(prev =>
      (prev[key] ?? -1) >= progress ? prev : { ...prev, [key]: progress }
    );
  };

  // ── Excel import handler ──────────────────────────────────────────────
  const handleExcelImport = (importedRows) => {
    const newComponents = importedRows.map(row => ({
      _key:                 row._key || `${Date.now()}-${Math.random()}`,
      id:                   null,
      articleNumber:        '',
      partName:             uppercaseImportedText(row.partName),
      partDescription:      uppercaseImportedText(row.partDescription),
      stockType:            normalizeStockType(row.stockType),
      partColor:            uppercaseImportedText(row.partColor),
      length:               row.length                || '',
      width:                row.width                 || '',
      height:               row.height                || '',
      measurementUnit:      normalizeMeasurementUnit(row.measurementUnit || 'INCHES'),
      partWeight:           uppercaseImportedText(row.partWeight),
      weightUnit:           normalizeWeightUnit(row.weightUnit),
      category:             normalizeCategory(row.category),
      vendorPartNumber:     uppercaseImportedText(row.vendorPartNumber),
      vendorFactoryName:    uppercaseImportedText(row.vendorFactoryName),
      supplierName:         uppercaseImportedText(row.supplierName),
      supplierPartNumber:   uppercaseImportedText(row.supplierPartNumber),
      uom:                  uppercaseImportedText(row.uom || 'EACH'),
      totalQtyInFg:         row.totalQtyInFg          !== '' ? row.totalQtyInFg : '',
      costPerUom:           row.costPerUom            !== '' ? row.costPerUom   : '',
      currency:             uppercaseImportedText(row.currency || 'USD'),
      pendingPhotos:        [],
    }));
    setComponents(prev => {
      // Drop any trailing empty placeholder rows before merging so imported
      // rows don't get pushed down below a blank starter row
      const existing = prev.filter(c => c.partName.trim() || c.vendorPartNumber.trim());
      return [...existing, ...newComponents];
    });
    setShowExcelUpload(false);
    toast.success(`${newComponents.length} row${newComponents.length !== 1 ? 's' : ''} imported from Excel`);
  };
  const [confirmRemoveKey, setConfirmRemoveKey] = useState(null); // _key of component pending removal

  const requestRemoveComponent = (key) => {
    setConfirmRemoveKey(key);
  };

  const confirmRemoveComponent = async () => {
    const key = confirmRemoveKey;
    setConfirmRemoveKey(null);
    const comp = components.find(c => c._key === key);
    if (!comp) return;

    if (comp.id) {
      // Persisted row — remove it from the backend (and its stored files).
      try {
        await vendorApi.deleteBomComponent(id, comp.id);
        comp.pendingPhotos?.forEach(p => URL.revokeObjectURL(p.url));
        setComponents(prev => prev.filter(c => c._key !== key));
        qc.invalidateQueries(['vendor-assignment', id]);
        toast.success('Component removed');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to remove component');
      }
    } else {
      // New row that has not yet been saved — remove locally.
      comp.pendingPhotos?.forEach(p => URL.revokeObjectURL(p.url));
      setComponents(prev => prev.filter(c => c._key !== key));
    }
  };

  const handleCategorySave = async (comp) => {
    if (!comp.id) return; // unsaved row — category goes with the draft save
    try {
      await vendorApi.updatePartArticleCategory(id, comp.id, comp.category);
      savedCategoriesRef.current = { ...savedCategoriesRef.current, [comp.id]: comp.category };
      toast.success('Category updated');
      qc.invalidateQueries(['vendor-assignment', id]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update category');
    }
  };

  // ── Main product photo handlers (multiple, max 4 total) ──────────────
  const handleMainPhotosAdd = (files) => {
    const savedCount = assignment?.partArticles?.find(p => p.mainProduct)?.files?.length || 0;
    const currentCount = savedCount + mainPendingPhotos.length;
    const remaining = MAX_PHOTOS - currentCount;
    if (remaining <= 0) {
      toast.error('Maximum 4 photos allowed for the main product');
      return;
    }
    const allowed = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.error(`Only ${remaining} more photo${remaining !== 1 ? 's' : ''} can be added (max ${MAX_PHOTOS})`);
    }
    const newPhotos = allowed.map(f => ({ file: f, url: URL.createObjectURL(f) }));
    setMainPendingPhotos(prev => [...prev, ...newPhotos]);
  };

  const handleMainPhotoRemove = (index) => {
    setMainPendingPhotos(prev => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSavedPhotoRemove = async (partArticleId, fileId) => {
    if (!partArticleId || removingPhotoIds.includes(fileId)) return;
    setRemovingPhotoIds(prev => [...prev, fileId]);
    try {
      await vendorApi.deleteFile(id, partArticleId, fileId);
      // Remove it immediately so the upload slot is available before the
      // background refetch finishes.
      qc.setQueryData(['vendor-assignment', id], current => {
        if (!current?.data?.partArticles) return current;
        return {
          ...current,
          data: {
            ...current.data,
            partArticles: current.data.partArticles.map(article =>
              String(article.id) === String(partArticleId)
                ? { ...article, files: (article.files || []).filter(file => String(file.id) !== String(fileId)) }
                : article
            ),
          },
        };
      });
      toast.success('Photo deleted');
      qc.invalidateQueries(['vendor-assignment', id]);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete photo');
    } finally {
      setRemovingPhotoIds(prev => prev.filter(id => id !== fileId));
    }
  };

  // ── BOM row photo handlers (multiple per row, max 4 total) ───────────
  const handleCompPhotosAdd = (key, files) => {
    setComponents(prev => prev.map(c => {
      if (c._key !== key) return c;
      const savedCount = assignment?.partArticles
        ?.find(p => p.id === c.id && !p.mainProduct)
        ?.files?.length || 0;
      const currentCount = savedCount + c.pendingPhotos.length;
      const remaining = MAX_PHOTOS - currentCount;
      if (remaining <= 0) {
        toast.error('Maximum 4 photos allowed per BOM component');
        return c;
      }
      const allowed = files.slice(0, remaining);
      if (files.length > remaining) {
        toast.error(`Only ${remaining} more photo${remaining !== 1 ? 's' : ''} can be added per component (max ${MAX_PHOTOS})`);
      }
      const newPhotos = allowed.map(f => ({ file: f, url: URL.createObjectURL(f) }));
      return { ...c, pendingPhotos: [...c.pendingPhotos, ...newPhotos] };
    }));
  };

  const handleCompPhotoRemove = (key, index) => {
    setComponents(prev => prev.map(c => {
      if (c._key !== key) return c;
      URL.revokeObjectURL(c.pendingPhotos[index].url);
      return { ...c, pendingPhotos: c.pendingPhotos.filter((_, i) => i !== index) };
    }));
  };

  // ── Submit: save draft → upload all pending photos → submit ──────────
  const handleSubmitRequest = async () => {
    const err = validateForm();
    if (err) { setErrorMessages(typeof err === 'string' ? [err] : err); return; }
    setConfirmSubmit(false);
    setSubmitting(true);
    try {
      // 1. Save draft to persist data + get article IDs
      const draftRes = await vendorApi.saveAsDraft(id, buildPayload());
      const savedParts = draftRes?.data?.partArticles || [];
      const savedMain  = savedParts.find(p => p.mainProduct);
      const savedBom   = savedParts.filter(p => !p.mainProduct);

      // 2. Upload all main product pending photos — clear each one immediately after success
      if (savedMain?.id) {
        for (const photo of mainPendingPhotos) {
          await vendorApi.uploadFile(savedMain.id, photo.file);
          setMainPendingPhotos(prev => prev.slice(1));
        }
      }

      // 3. Upload all pending photos per BOM component — clear each one immediately after success
      for (let i = 0; i < components.length; i++) {
        const comp = components[i];
        if (savedBom[i]?.id) {
          for (const photo of comp.pendingPhotos) {
            await vendorApi.uploadFile(savedBom[i].id, photo.file);
            setComponents(prev => prev.map((c, idx) =>
              idx === i ? { ...c, pendingPhotos: c.pendingPhotos.slice(1) } : c
            ));
          }
        }
      }

      // 4. Submit assignment
      await vendorApi.submitAssignment(id);
      toast.success('Request submitted to OmniDrive for review!');
      qc.invalidateQueries(['vendor-assignment', id]);
      navigate('/vendor/assignments');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (!assignment) return <div className="text-center py-16 text-gray-400">Assignment not found</div>;

  const savedMainArticle = assignment.partArticles?.find(p => p.mainProduct);
  const hasMainPhoto     = (savedMainArticle?.files?.length || 0) + mainPendingPhotos.length > 0;
  const savedCompsMap    = Object.fromEntries(
    (assignment.partArticles?.filter(p => !p.mainProduct) || []).map(c => [c.id, c])
  );
  const hasAllCompPhotos = components.length > 0 && components.every(c => {
    const savedCount = (c.id ? savedCompsMap[c.id]?.files?.length : 0) || 0;
    return savedCount + c.pendingPhotos.length > 0;
  });

  return (
    <div className="w-full pb-24">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1">
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {assignment.finishedProduct || assignment.fgName || `Assignment #${id}`}
          </h1>
          <p className="text-sm text-gray-400 font-mono mt-0.5">{assignment.bobFinishedGoodCode || assignment.fgName} · Assignment #{id}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {assignment.isDraft && (
            <span className="px-2.5 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">Draft</span>
          )}
          <StatusBadge status={assignment.status} />
        </div>
      </div>

      {/* ── Lifecycle Timeline ──────────────────────────────────────────── */}
      <div className="mb-6">
        <StatusTimeline
          fgCreatedAt={assignment.fgCreatedAt}
          assignedAt={assignment.assignedAt}
          submittedAt={assignment.submittedAt}
          reviewedAt={assignment.reviewedAt}
          fgClientApprovedAt={assignment.fgClientApprovedAt}
          status={assignment.status}
          companyNotes={assignment.companyNotes}
          assignmentId={Number(id)}
          fetchHistory={vendorApi.getAssignmentHistory}
          companyLabel="OmniDrive"
          clientLabel="Account"
          viewerIsVendor
        />
      </div>

      {assignment.companyNotes && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl mb-6">
          <p className="text-xs font-semibold text-orange-700 mb-1">{assignment.status === 'CLIENT_REJECTED' ? 'Account Rejection Notes' : assignment.status === 'CVA_REJECTED' ? 'CVA Rejection Notes' : 'OmniDrive Review Notes'}</p>
          <p className="text-sm text-orange-800">{assignment.companyNotes}</p>
        </div>
      )}

      {/* ── Lifecycle Dates ────────────────────────────────────────────── */}
      <LifecycleDates assignment={assignment} />

      {/* ── Finished Good Details (read-only) ──────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <span className="text-lg">📦</span>
          <h2 className="text-lg font-bold text-gray-900">Finished Good Details</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">main product</span>
        </div>

        {/* ── FG Catalog Fields (read-only from Finished Good record) ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
          <ReadField label="Product Title"                  value={assignment.requestTitle || assignment.fgName} />
          <ReadField label="Finished Good Code"             value={assignment.bobFinishedGoodCode} />
          <ReadField label="Country of Origin"              value={assignment.countryOfOrigin} />
          <ReadField label="Top Category"                   value={assignment.topCategory} />
          <ReadField label="Category"                       value={assignment.category} />
          <ReadField label="Collection Name"                value={assignment.collectionName} />
          <ReadField label="Finished Product"               value={assignment.finishedProduct} />
          <ReadField label="Vendor Finished Good Model"     value={assignment.vendorFinishedGoodModel} />
          <ReadField label="Factory Code"                   value={assignment.factoryCode} />
          <ReadField label="Factory Name"                   value={assignment.factoryName} />
          <ReadField label="Finish"                         value={assignment.finish} />
          <ReadField label="Color"                          value={assignment.color} />
          <ReadField label="Vendor Product Code"            value={assignment.vendorProductCode} />
          <ReadField label="Package Sales Set"              value={assignment.packageSalesSet} />
          <ReadField label="Package Sales Set Name"         value={assignment.packageSalesSetName} />
          <ReadField label="Package Component Qty"          value={assignment.packageComponentQty} />
          <ReadField label="Vendor Code (Internal to Bobs)" value={assignment.vendorCode} highlight />
          <ReadField label="Vendor Name"                    value={assignment.fgVendorName} />
        </div>
        {assignment.productDescription && (
          <div className="mb-4">
            <ReadField label="Product Description" value={assignment.productDescription} />
          </div>
        )}

        <div className="border-t border-gray-100 pt-4 mt-2 mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Vendor-Submitted Main Product Info</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <ReadField label="Finished Good Article ID" value={mainProduct.fgArticleId} />
            <ReadField label="Collection Name"           value={mainProduct.collectionName} />
            <ReadField label="Product Description"       value={mainProduct.productDescription} />
            <ReadField label="Article Group"             value={mainProduct.articleGroup} />
            <ReadField label="Style / Generic"           value={mainProduct.genericStyle} />
          </div>
        </div>

        {/* Main product photos — up to 4 */}
        <div>
          {(() => {
            const totalMainPhotos = (savedMainArticle?.files?.length || 0) + mainPendingPhotos.length;
            const mainPhotosRemaining = MAX_PHOTOS - totalMainPhotos;
            return (
              <>
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Main Product Photo <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 font-normal ml-2">
                    Up to {MAX_PHOTOS} photos
                    {canEdit && (
                      <span className={`ml-1 font-medium ${mainPhotosRemaining <= 0 ? 'text-red-400' : 'text-blue-400'}`}>
                        ({mainPhotosRemaining > 0 ? `${mainPhotosRemaining} remaining` : 'limit reached'})
                      </span>
                    )}
                  </span>
                </p>
                <div className="flex gap-4 flex-wrap items-start">
                  {/* Already-uploaded photos from server */}
                  {savedMainArticle?.files?.map((f, i) => (
                    <div key={`saved-${i}`} className="relative">
                      <PhotoThumb fileId={f.id} label={f.originalName} />
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleSavedPhotoRemove(savedMainArticle.id, f.id)}
                          disabled={removingPhotoIds.includes(f.id)}
                          aria-label={`Delete ${f.originalName}`}
                          title="Delete photo"
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 disabled:opacity-60 disabled:cursor-wait">
                          {removingPhotoIds.includes(f.id) ? '…' : '✕'}
                        </button>
                      )}
                    </div>
                  ))}
                  {/* Local pending previews */}
                  {mainPendingPhotos.map((p, i) => (
                    <div key={i} className="relative">
                      <PhotoThumb src={p.url} label={`New photo ${i + 1} (pending)`} pending />
                      {canEdit && (
                        <button onClick={() => handleMainPhotoRemove(i)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600">
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  {/* Drop zone — hidden once limit is reached */}
                  {canEdit && mainPhotosRemaining > 0 && (
                    <PhotoDropZone onFiles={handleMainPhotosAdd} remaining={mainPhotosRemaining} />
                  )}
                </div>
                {/* Warning shown when no photo has been uploaded */}
                {canEdit && !hasMainPhoto && (
                  <p className="mt-3 text-xs text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5">
                    ⚠ Main Product Photo is required before submitting
                  </p>
                )}
                <p className="mt-3 text-xs font-bold text-red-600">
                  * All fields are mandatory
                </p>
              </>
            );
          })()}
        </div>
      </div>

      {/* ── BOM Table ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚙️</span>
            <h2 className="text-lg font-bold text-gray-900">Components (Bill of Materials)</h2>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canDownloadSubmittedBom && (
              <button
                onClick={handleDownloadBom}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                📥 Download BOM
              </button>
            )}
            {canEdit && (
              <>
              <button
                onClick={() => vendorApi.downloadPartArticleTemplate().catch(() => toast.error('Could not download the template. Please try again.'))}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors">
                ⬇ Download Template
              </button>
              <button
                onClick={() => setShowExcelUpload(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm rounded-lg font-medium hover:bg-green-700 transition-colors">
                📊 Upload Excel
              </button>
              <button onClick={addComponent}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors">
                + Add Component
              </button>
              </>
            )}
          </div>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="w-full text-sm border-collapse" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr className="bg-gray-50 border-b-2 border-gray-200">
                <Th w="40px">#</Th>
                <Th required>Part Name</Th>
                <Th required>Part Description</Th>
                <Th required w="110px">Stock Type</Th>
                <Th required>Part Color</Th>
                <Th required w="90px">Measurement Unit</Th>
                <Th required w="80px">L (Length)</Th>
                <Th required w="80px">W (Width)</Th>
                <Th required w="80px">H (Height)</Th>
                <Th required w="140px">Weight Unit</Th>
                <Th required>Weight</Th>
                <Th required w="200px">Category</Th>
                <Th required>Vendor Part Number</Th>
                <Th required>Vendor's Factory Name</Th>
                <Th required>Supplier Name</Th>
                <Th required>Supplier Part Number</Th>
                <Th required w="100px">UoM</Th>
                <Th required w="110px">Total Qty in FG</Th>
                <Th required w="130px">Cost per UoM (USD)</Th>
                <Th required w="160px">Photos</Th>
                {canEdit && <Th w="70px"></Th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {components.map((comp, idx) => {
                const savedComp = comp.id ? savedCompsMap[comp.id] : null;
                return (
                  <BomRow
                    key={comp._key}
                    index={idx}
                    comp={comp}
                    canEdit={canEdit}
                    canEditCategory={canEditCategory}
                    categoryDirty={comp.id != null && comp.category !== (savedCategoriesRef.current[comp.id] ?? comp.category)}
                    validationProgress={fieldProgress[comp._key] ?? -1}
                    savedFiles={savedComp?.files || []}
                    onChange={(field, val) => updateComp(comp._key, field, val)}
                    onFieldFocus={(field) => markFieldProgress(comp._key, field)}
                    onFieldBlur={(field) => markFieldProgress(comp._key, field, true)}
                    onRemove={() => requestRemoveComponent(comp._key)}
                    onCategorySave={() => handleCategorySave(comp)}
                    onPhotosAdd={(files) => handleCompPhotosAdd(comp._key, files)}
                    onPhotoRemove={(index) => handleCompPhotoRemove(comp._key, index)}
                    onSavedPhotoRemove={(fileId) => handleSavedPhotoRemove(comp.id, fileId)}
                    removingPhotoIds={removingPhotoIds}
                  />
                );
              })}
              {components.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 22 : 21}
                    className="px-4 py-10 text-center text-gray-400 text-sm">
                    {canEdit
                      ? 'No components yet — click "+ Add Component" to start.'
                      : 'No BOM components recorded for this assignment.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Fixed action bar ──────────────────────────────────────────── */}
      {canEdit && (
        <div className="fixed bottom-0 left-60 right-0 bg-white border-t border-gray-200 px-8 py-4 flex items-center justify-end shadow-lg z-20">
          <div className="flex gap-3">
            <button
              onClick={handleSaveForLater}
              disabled={savingDraft || submitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm">
              💾 Save for Later
            </button>
            <button
              onClick={() => setConfirmSubmit(true)}
              disabled={!isFormValid || !hasMainPhoto || !hasAllCompPhotos || submitting || savingDraft}
              title={!hasMainPhoto ? 'Upload a Main Product Photo first' : !hasAllCompPhotos ? 'Add a photo for each BOM component' : !isFormValid ? 'Fill all required fields first' : ''}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white text-sm rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm">
              ✈️ {submitting ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </div>
      )}

      {!canEdit && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl text-center text-sm text-gray-500">
          {userRole === 'VENDOR_VIEWER' && <p>Your role has view-only access to this setup sheet.</p>}
          {assignment.status === 'VENDOR_SUBMITTED' && 'Your submission is awaiting OmniDrive review. You will be notified once reviewed.'}
          {assignment.status === 'COMPANY_APPROVED'        && 'OmniDrive has approved this submission. It is now with the Account for review.'}
          {assignment.status === 'PENDING_FINAL_APPROVAL'  && '⏳ Account has approved. Awaiting CVA final sign-off before Salesforce sync.'}
          {assignment.status === 'CLIENT_APPROVED'         && '✅ This submission has been fully approved and synced to Salesforce.'}
          {assignment.status === 'CLIENT_REJECTED'         && 'The account has requested revisions. Please update the details and resubmit.'}
          {assignment.status === 'CANCELLED'               && '🚫 This process has been permanently cancelled by OmniDrive. No further action is required.'}
          {!['VENDOR_SUBMITTED','COMPANY_APPROVED','PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED','CANCELLED'].includes(assignment.status) &&
            <>Status: <strong>{statusLabel(assignment.status)}</strong></>}
        </div>
      )}

      {/* ── Notes thread ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Notes
          <span className="text-xs text-gray-400 font-normal">— visible to OmniDrive, vendor &amp; account</span>
        </h3>
        <NoteThread
          assignmentId={Number(id)}
          fetchNotes={vendorApi.getAssignmentNotes}
          postNote={vendorApi.addAssignmentNote}
          canPost={userRole !== 'VENDOR_VIEWER' && !!assignment && assignment.status !== 'CANCELLED'}
          queryKey="vendor-notes"
        />
      </div>

      {/* ── Audit Log ──────────────────────────────────────────────────── */}
      <div className="mt-6">
        <AuditLogPanel
          assignmentId={Number(id)}
          fetchHistory={vendorApi.getAssignmentHistory}
          queryKey="vendor-audit-log"
        />
      </div>

      {/* ── Excel Upload Modal ──────────────────────────────────────────── */}
      {showExcelUpload && (
        <PartArticleExcelUpload
          assignmentId={Number(id)}
          fgNumber={assignment?.fgNumber || ''}
          mainProduct={mainProduct}
          onImport={handleExcelImport}
          onClose={() => setShowExcelUpload(false)}
          onDraftSaved={() => {
            qc.invalidateQueries(['vendor-assignment', id]);
            setShowExcelUpload(false);
          }}
        />
      )}

      {/* ── Validation Error Modal ─────────────────────────────────────── */}
      {errorMessages && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Please fix the following errors</h2>
                <p className="text-sm text-gray-500">All required fields must be filled before submitting.</p>
              </div>
            </div>
            <ul className="mb-5 space-y-2 max-h-64 overflow-y-auto">
              {(Array.isArray(errorMessages) ? errorMessages : [errorMessages]).map((msg, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">
                  <span className="mt-0.5 flex-shrink-0">•</span>
                  {msg}
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button
                onClick={() => setErrorMessages(null)}
                className="px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">
                OK, fix errors
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove BOM component confirmation ────────────────────────── */}
      {confirmRemoveKey && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove Component?</h3>
            <p className="text-sm text-gray-600 mb-5">
              This will permanently delete this BOM row and all its uploaded photos. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmRemoveKey(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={confirmRemoveComponent}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rich confirmation modal ───────────────────────────────────── */}
      {confirmSubmit && (
        <SubmitConfirmModal
          mainProduct={mainProduct}
          mainPendingPhotos={mainPendingPhotos}
          mainSavedFiles={savedMainArticle?.files || []}
          components={components}
          savedCompsMap={savedCompsMap}
          onConfirm={handleSubmitRequest}
          onCancel={() => setConfirmSubmit(false)}
          submitting={submitting}
        />
      )}
    </div>
  );
}

// ── BOM Row ─────────────────────────────────────────────────────────────────

function BomRow({ index, comp, canEdit, canEditCategory, categoryDirty, validationProgress, savedFiles, onChange, onFieldFocus, onFieldBlur, onRemove, onCategorySave, onPhotosAdd, onPhotoRemove, onSavedPhotoRemove, removingPhotoIds }) {
  const fileRef = useRef();
  const showMissing = (field) => {
    const fieldIndex = BOM_FIELD_ORDER.indexOf(field);
    if (fieldIndex >= validationProgress) return false;

    // Length, width, and height are one required dimension group: at least
    // one must be present, so flag the first cell instead of all three.
    if (['length', 'width', 'height'].includes(field)) {
      return field === 'length' && !comp.length && !comp.width && !comp.height;
    }
    return !comp[field];
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length) onPhotosAdd(files);
  }, [onPhotosAdd]);

  return (
    <tr className="align-top hover:bg-gray-50/60 transition-colors">
      {/* # */}
      <td className="px-3 py-2 text-gray-400 text-xs font-medium">{index + 1}</td>

      {/* Part Name * */}
      <td className="px-2 py-2">
        <TCell value={comp.partName} disabled={!canEdit} required showMissing={showMissing('partName')}
          onBlur={() => onFieldBlur('partName')}
          placeholder="e.g. Seat Motor" onChange={v => onChange('partName', v)} />
      </td>

      {/* Part Description * */}
      <td className="px-2 py-2">
        <TCell value={comp.partDescription} disabled={!canEdit} required showMissing={showMissing('partDescription')}
          onBlur={() => onFieldBlur('partDescription')}
          placeholder="e.g. Seat Motor (Dummy Model X1)" onChange={v => onChange('partDescription', v)} />
      </td>

      {/* Stock Type * */}
      <td className="px-2 py-2">
        {canEdit
          ? <select value={comp.stockType} onChange={e => onChange('stockType', e.target.value)} onFocus={() => onFieldFocus('stockType')} onBlur={() => onFieldBlur('stockType')}
              className={`w-full px-2 py-1.5 border rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500
                ${!comp.stockType ? 'border-orange-300' : 'border-gray-300'}`}>
              <option value="">SELECT…</option>
              {STOCK_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
              ${comp.stockType === 'SPECIAL' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
              {comp.stockType || '—'}
            </span>
        }
      </td>

      {/* Part Color * */}
      <td className="px-2 py-2">
        <TCell value={comp.partColor} disabled={!canEdit} required alphabeticOnly showMissing={showMissing('partColor')}
          onBlur={() => onFieldBlur('partColor')}
          placeholder="e.g. Red" onChange={v => onChange('partColor', v)} />
      </td>

      {/* Measurement Unit */}
      <td className="px-2 py-2">
        <select value={comp.measurementUnit} disabled={!canEdit}
          onChange={e => onChange('measurementUnit', e.target.value)}
          onFocus={() => onFieldFocus('measurementUnit')} onBlur={() => onFieldBlur('measurementUnit')}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm uppercase bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500">
          {MEASUREMENT_UNIT_OPTIONS.map(unit => <option key={unit} value={unit}>{unit.toUpperCase()}</option>)}
        </select>
      </td>

      {/* Length * */}
      <td className="px-2 py-2">
        <TCell value={comp.length} disabled={!canEdit} numericOnly showMissing={showMissing('length')}
          required={true}
          onBlur={() => onFieldBlur('length')}
          placeholder="e.g. 20" onChange={v => onChange('length', v)} />
      </td>

      {/* Width * */}
      <td className="px-2 py-2">
        <TCell value={comp.width} disabled={!canEdit} numericOnly showMissing={showMissing('width')}
          required={true}
          onBlur={() => onFieldBlur('width')}
          placeholder="e.g. 30" onChange={v => onChange('width', v)} />
      </td>

      {/* Height * */}
      <td className="px-2 py-2">
        <TCell value={comp.height} disabled={!canEdit} numericOnly showMissing={showMissing('height')}
          required={true}
          onBlur={() => onFieldBlur('height')}
          placeholder="e.g. 10" onChange={v => onChange('height', v)} />
      </td>

      {/* Weight Unit * */}
      <td className="px-2 py-2">
        {canEdit
          ? <select value={comp.weightUnit} onChange={e => onChange('weightUnit', e.target.value)} onFocus={() => onFieldFocus('weightUnit')} onBlur={() => onFieldBlur('weightUnit')}
              className={`w-full px-2 py-1.5 border rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500
                ${!comp.weightUnit ? 'border-orange-300' : 'border-gray-300'}`}>
              <option value="">SELECT…</option>
              {WEIGHT_UNIT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label.toUpperCase()}</option>
              ))}
            </select>
          : <span className="text-sm text-gray-700 uppercase">
              {(WEIGHT_UNIT_OPTIONS.find(o => o.value === comp.weightUnit)?.label || comp.weightUnit || '—').toUpperCase()}
            </span>
        }
      </td>

      {/* Weight * */}
      <td className="px-2 py-2">
        <TCell value={comp.partWeight} disabled={!canEdit} required numericOnly showMissing={showMissing('partWeight')}
          onBlur={() => onFieldBlur('partWeight')}
          placeholder="e.g. 500" onChange={v => onChange('partWeight', v)} />
      </td>

      {/* Category * */}
      <td className="px-2 py-2">
        {canEditCategory
          ? <div className="flex flex-col gap-1">
              <select
                value={comp.category}
                onChange={e => onChange('category', e.target.value)}
                onFocus={() => onFieldFocus('category')}
                onBlur={() => onFieldBlur('category')}
                className={`w-full px-2 py-1.5 border rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${!comp.category ? (showMissing('category') ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-orange-300') : 'border-gray-300'}`}
                style={{ minWidth: 180 }}>
                <option value="">SELECT CATEGORY…</option>
                {CATEGORY_OPTIONS.map(({ group, items }) => (
                  <optgroup key={group} label={group.toUpperCase()}>
                    {items.map(item => (
                      <option key={item} value={`${group} > ${item}`}>{item.toUpperCase()}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {showMissing('category') && (
                <p className="text-xs text-red-600 font-medium leading-tight">Category is required</p>
              )}
              {categoryDirty && (
                <button
                  onClick={onCategorySave}
                  className="self-start px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                  Save
                </button>
              )}
            </div>
          : <span className="text-sm text-gray-700 uppercase">{comp.category || '—'}</span>
        }
      </td>

      {/* Vendor Part Number * */}
      <td className="px-2 py-2">
        <TCell value={comp.vendorPartNumber} disabled={!canEdit} required showMissing={showMissing('vendorPartNumber')}
          placeholder="e.g. MTR-DM-101" onFocus={() => onFieldFocus('vendorPartNumber')} onBlur={() => onFieldBlur('vendorPartNumber')}
          onChange={v => onChange('vendorPartNumber', v)} />
      </td>

      {/* Vendor's Factory Name */}
      <td className="px-2 py-2">
        <TCell value={comp.vendorFactoryName} disabled={!canEdit} required showMissing={showMissing('vendorFactoryName')}
          onFocus={() => onFieldFocus('vendorFactoryName')} onBlur={() => onFieldBlur('vendorFactoryName')}
          placeholder="e.g. Shenzhen Factory A" onChange={v => onChange('vendorFactoryName', v)} />
      </td>

      {/* Supplier Name * */}
      <td className="px-2 py-2">
        <TCell value={comp.supplierName} disabled={!canEdit} required showMissing={showMissing('supplierName')}
          onFocus={() => onFieldFocus('supplierName')} onBlur={() => onFieldBlur('supplierName')}
          placeholder="e.g. OmniMotion" onChange={v => onChange('supplierName', v)} />
      </td>

      {/* Supplier Part Number * */}
      <td className="px-2 py-2">
        <TCell value={comp.supplierPartNumber} disabled={!canEdit} required showMissing={showMissing('supplierPartNumber')}
          onFocus={() => onFieldFocus('supplierPartNumber')} onBlur={() => onFieldBlur('supplierPartNumber')}
          placeholder="e.g. OM-882" onChange={v => onChange('supplierPartNumber', v)} />
      </td>

      {/* UoM * */}
      <td className="px-2 py-2">
        {canEdit
          ? <select value={comp.uom} onChange={e => onChange('uom', e.target.value)} onFocus={() => onFieldFocus('uom')} onBlur={() => onFieldBlur('uom')}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500">
              {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
            </select>
          : <span className="text-sm text-gray-700">{comp.uom || '—'}</span>
        }
      </td>

      {/* Total Qty in FG * */}
      <td className="px-2 py-2">
        <div className="w-full">
        {canEdit
          ? <input type="number" min="1" value={comp.totalQtyInFg}
              onChange={e => onChange('totalQtyInFg', e.target.value)}
              onFocus={() => onFieldFocus('totalQtyInFg')} onBlur={() => onFieldBlur('totalQtyInFg')}
              placeholder="e.g. 2"
              className={`w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                ${!comp.totalQtyInFg ? (showMissing('totalQtyInFg') ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-orange-300') : 'border-gray-300'}`} />
          : <span className="text-sm text-gray-700">{comp.totalQtyInFg || '—'}</span>
        }
          {canEdit && showMissing('totalQtyInFg') && (
            <p className="mt-0.5 text-xs text-red-600 font-medium leading-tight">Total Qty in FG is required</p>
          )}
        </div>
      </td>
      {/* Cost per UoM * */}
      <td className="px-2 py-2">
        <div className="w-full">
        {canEdit
          ? <input type="number" min="0" step="0.01" value={comp.costPerUom}
              onChange={e => onChange('costPerUom', e.target.value)}
              onFocus={() => onFieldFocus('costPerUom')} onBlur={() => onFieldBlur('costPerUom')}
              placeholder="0.00"
              className={`w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                ${!comp.costPerUom ? (showMissing('costPerUom') ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-orange-300') : 'border-gray-300'}`} />
          : <span className="text-sm text-gray-700">{comp.costPerUom ? `$${comp.costPerUom}` : '—'}</span>
        }
          {canEdit && showMissing('costPerUom') && (
            <p className="mt-0.5 text-xs text-red-600 font-medium leading-tight">Cost per UoM is required</p>
          )}
        </div>
      </td>
      {/* Photos — up to 4 per row */}
      <td className="px-2 py-2">
        <div className="flex flex-col gap-1.5 items-start">
          {/* Server-saved photos */}
          {savedFiles.map((f, i) => (
            <div key={`s${i}`} className="relative">
              <PhotoThumb fileId={f.id} label={f.originalName} small />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onSavedPhotoRemove(f.id)}
                  disabled={removingPhotoIds.includes(f.id)}
                  aria-label={`Delete ${f.originalName}`}
                  title="Delete photo"
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 disabled:opacity-60 disabled:cursor-wait leading-none">
                  {removingPhotoIds.includes(f.id) ? '…' : '✕'}
                </button>
              )}
            </div>
          ))}

          {/* Local pending previews */}
          {comp.pendingPhotos.map((p, i) => (
            <div key={i} className="relative">
              <PhotoThumb src={p.url} label={`Pending ${i + 1}`} pending small />
              {canEdit && (
                <button onClick={() => onPhotoRemove(i)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 leading-none">
                  ✕
                </button>
              )}
            </div>
          ))}

          {/* Upload zone — hidden once 4-photo limit is reached */}
          {(() => {
            const totalPhotos = savedFiles.length + comp.pendingPhotos.length;
            const remaining = MAX_PHOTOS - totalPhotos;
            if (!canEdit) return null;
            if (remaining <= 0) {
              return (
                <span className="text-xs text-red-400 font-medium">Max 4 photos</span>
              );
            }
            return (
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1 border border-dashed border-gray-300 rounded-md px-2 py-1 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors text-xs text-gray-400 select-none whitespace-nowrap">
                📷 Add photos
                <span className="text-gray-300 ml-1">({remaining} left)</span>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files);
                    if (files.length) onPhotosAdd(files);
                    e.target.value = '';
                  }} />
              </div>
            );
          })()}
        </div>
      </td>

      {/* Remove */}
      {canEdit && (
        <td className="px-2 py-2">
          <button onClick={onRemove}
            className="text-red-400 hover:text-red-600 text-xs font-medium transition-colors whitespace-nowrap">
            ✕ Remove
          </button>
        </td>
      )}
    </tr>
  );
}

// ── Shared helpers ───────────────────────────────────────────────────────────

function Th({ children, required, w }) {
  return (
    <th style={w ? { width: w, minWidth: w } : { minWidth: '120px' }}
      className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </th>
  );
}

function TCell({ value, onChange, onFocus, onBlur, placeholder, disabled, required, numericOnly = false, alphabeticOnly = false, showMissing = false }) {
  const [touched, setTouched] = React.useState(false);

  const isEmpty = !value?.trim();
  const hasDashOrSpecial = !isEmpty && (isDashValue(value) || isSpecialCharsOnly(value));
  const hasNonAlphabetic = alphabeticOnly && !isEmpty && !isAlphabeticText(value);

  // Show required error only after user has touched the field
  const showRequiredError = (touched || showMissing) && required && isEmpty;
  const showContentError  = hasDashOrSpecial; // show immediately when invalid content typed

  const errorMsg = showRequiredError
    ? 'This field is required'
    : showContentError
      ? isDashValue(value) ? 'Dashes not allowed' : 'Special characters only not allowed'
      : hasNonAlphabetic
        ? 'Letters only'
      : null;

  const borderClass = disabled
    ? 'bg-gray-50 text-gray-600 border-gray-200 cursor-not-allowed'
    : errorMsg
      ? 'border-red-400 bg-red-50 focus:ring-red-400'
      : required && isEmpty && !touched
        ? 'border-orange-300'
        : 'border-gray-300';

  return (
    <div className="w-full">
      <input
        value={value}
        inputMode={numericOnly ? 'decimal' : undefined}
        onFocus={onFocus}
        onChange={e => {
          const nextValue = e.target.value;
          if (numericOnly && !isEditableDecimal(nextValue)) return;
          if (alphabeticOnly && !/^[a-zA-Z ]*$/.test(nextValue)) return;
          if (numericOnly || alphabeticOnly) {
            onChange(numericOnly ? nextValue : nextValue.toUpperCase());
          } else {
            onChange(nextValue.toUpperCase());
          }
        }}
        onBlur={() => {
          setTouched(true);
          onBlur?.();
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-2 py-1.5 border rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 ${borderClass}`}
      />
      {errorMsg && (
        <p className="mt-0.5 text-xs text-red-600 font-medium leading-tight">{errorMsg}</p>
      )}
    </div>
  );
}

function LifecycleDates({ assignment }) {
  const fmt = (d) => d ? format(new Date(d), 'dd MMM yyyy, hh:mm a') : '—';
  const reviewLabel = () => {
    if (!assignment.reviewedAt) return '—';
    if (['COMPANY_APPROVED', 'CLIENT_APPROVED'].includes(assignment.status)) return `Approved · ${fmt(assignment.reviewedAt)}`;
    if (['COMPANY_REJECTED', 'CLIENT_REJECTED', 'CVA_REJECTED'].includes(assignment.status))  return `Rejected · ${fmt(assignment.reviewedAt)}`;
    return fmt(assignment.reviewedAt);
  };

  const dates = [
    { icon: '🔄', label: 'FG Synced',            value: fmt(assignment.fgCreatedAt) },
    { icon: '📋', label: 'Assigned to Vendor',    value: fmt(assignment.assignedAt) },
    { icon: '📤', label: 'Vendor Submitted',       value: fmt(assignment.submittedAt) },
    { icon: '✅', label: 'OmniDrive Reviewed',     value: reviewLabel() },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Key Dates
      </h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {dates.map(({ icon, label, value }) => (
          <div key={label} className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{icon} {label}</span>
            <span className="text-sm text-gray-800 font-medium">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReadField({ label, value, highlight }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm min-h-[36px] break-words">
        {value != null && value !== ''
          ? <span className={highlight ? 'text-orange-600 font-medium' : 'text-gray-700'}>{value}</span>
          : <span className="text-gray-400">—</span>}
      </div>
    </div>
  );
}

function PhotoDropZone({ onFiles, remaining }) {
  const fileRef = useRef();
  const [dragging, setDragging] = useState(false);

  return (
    <div
      onDrop={e => {
        e.preventDefault();
        setDragging(false);
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        if (files.length) onFiles(files);
      }}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onClick={() => fileRef.current?.click()}
      className={`w-48 h-28 flex flex-col items-center justify-center border-2 border-dashed rounded-xl cursor-pointer transition-colors select-none
        ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/40'}`}>
      <span className="text-2xl mb-1">📷</span>
      <p className="text-xs text-gray-400 text-center leading-snug px-2">
        Drag & drop or<br /><span className="text-blue-500">click to browse</span>
      </p>
      <p className="text-xs text-gray-300 mt-0.5">
        {remaining != null ? `${remaining} of ${MAX_PHOTOS} remaining` : `up to ${MAX_PHOTOS} photos`}
      </p>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
        onChange={e => {
          const files = Array.from(e.target.files);
          if (files.length) onFiles(files);
          e.target.value = '';
        }} />
    </div>
  );
}

// ── Submit Confirmation Modal ────────────────────────────────────────────────

function SubmitConfirmModal({ mainProduct, mainPendingPhotos, mainSavedFiles, components, savedCompsMap, onConfirm, onCancel, submitting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col"
        style={{ width: '92vw', maxWidth: 960, maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Confirm Submission</h2>
            <p className="text-xs text-gray-400 mt-0.5">Please review all details before submitting. This cannot be undone.</p>
          </div>
          <button onClick={onCancel} disabled={submitting}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2 disabled:opacity-40">✕</button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          {/* Main product */}
          <section>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <span>📦</span> Main Product
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              {[
                ['FG Article ID',        mainProduct.fgArticleId],
                ['Collection Name',      mainProduct.collectionName],
                ['Product Description',  mainProduct.productDescription],
                ['Article Group',        mainProduct.articleGroup],
                ['Style / Generic',      mainProduct.genericStyle],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
                  <p className="text-sm text-gray-800 font-medium">{value || <span className="text-gray-300">—</span>}</p>
                </div>
              ))}
            </div>
            {/* Main product photos */}
            <div className="flex gap-3 flex-wrap">
              {mainSavedFiles.map((f, i) => (
                <PhotoThumb key={`ms${i}`} fileId={f.id} label={f.originalName} />
              ))}
              {mainPendingPhotos.map((p, i) => (
                <PhotoThumb key={`mp${i}`} src={p.url} label={`Pending upload ${i + 1}`} pending />
              ))}
              {mainPendingPhotos.length === 0 && mainSavedFiles.length === 0 && (
                <p className="text-xs text-red-500 font-medium">⚠ No photo uploaded — submission blocked</p>
              )}
            </div>
          </section>

          {/* BOM table */}
          <section>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <span>⚙️</span> Bill of Materials ({components.length} component{components.length !== 1 ? 's' : ''})
            </h3>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['#', 'Part Name', 'Part Description', 'Stock Type', 'Part Color', 'Measurement Unit', 'L', 'W', 'H', 'Weight Unit', 'Weight', 'Category', 'Vendor Part No.', "Vendor's Factory", 'Supplier', 'Supplier Part No.', 'UoM', 'Qty in FG', 'Cost / UoM', 'Photos'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {components.map((c, idx) => {
                    const saved = c.id ? savedCompsMap[c.id] : null;
                    const photos = saved?.files || [];
                    return (
                      <tr key={c._key} className="align-middle">
                        <td className="px-3 py-2 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{c.partName || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{c.partDescription || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                            ${c.stockType === 'SPECIAL' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                            {c.stockType || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{c.partColor || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{normalizeMeasurementUnit(c.measurementUnit || 'INCHES')}</td>
                        <td className="px-3 py-2 text-gray-600">{c.length || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{c.width || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{c.height || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {(WEIGHT_UNIT_OPTIONS.find(o => o.value === c.weightUnit)?.label || c.weightUnit || '—').toUpperCase()}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{c.partWeight || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap uppercase">{c.category || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 text-sm uppercase">{c.vendorPartNumber || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{c.vendorFactoryName || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{c.supplierName || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 text-sm uppercase">{c.supplierPartNumber || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{c.uom}</td>
                        <td className="px-3 py-2 text-gray-700 text-right">{c.totalQtyInFg}</td>
                        <td className="px-3 py-2 text-gray-700 text-right font-mono">${Number(c.costPerUom || 0).toFixed(2)} <span className="text-gray-400 text-xs">{c.currency}</span></td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 flex-wrap">
                            {photos.map((f, i) => (
                              <PhotoThumb key={i} fileId={f.id} label={f.originalName} small />
                            ))}
                            {c.pendingPhotos.map((p, i) => (
                              <PhotoThumb key={`pp${i}`} src={p.url} label={`Pending ${i + 1}`} pending small />
                            ))}
                            {c.pendingPhotos.length === 0 && photos.length === 0 && (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            <span className="text-lg leading-none">⚠️</span>
            <p>Once submitted, this request will be sent to <strong>OmniDrive</strong> for review and <strong>cannot be edited</strong> until OmniDrive responds.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={onCancel} disabled={submitting}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 text-sm rounded-lg font-medium hover:bg-white disabled:opacity-40 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={submitting}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white text-sm rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm">
            {submitting
              ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> Submitting…</>
              : '✈️ Confirm & Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// PhotoThumb is imported from ../../components/common/PhotoThumb
// It fetches server-saved images via the backend (fileId prop) so the S3
// bucket does not need to be public.  Local pending previews use src prop.
